import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import LiveTvIcon from '@mui/icons-material/LiveTv';
import PeopleIcon from '@mui/icons-material/People';
import DarkModeRoundedIcon from '@mui/icons-material/DarkModeRounded';
import LightModeRoundedIcon from '@mui/icons-material/LightModeRounded';
import ChatModal from './chat/ChatModal';
import CallUI from './CallUI';
import LiveStream from '../components/LiveStream';
import FileTransfer from './FileTransfer';
import FriendsPanel from './FriendsPanel';
import { FriendAvatar } from './FriendTile';
import { useAuth } from '../contexts/AuthContext';
import Match from "./Match";

/* ------------------------------------------------------------------ */
/* Styles (colors come from the --mf-* variables defined in Feed.js)    */
/* ------------------------------------------------------------------ */
const NAVBAR_CSS = `
.mf-nav{display:flex;align-items:center;gap:6px;min-width:0}
.mf-nav-sep{width:1px;height:24px;background:var(--mf-border-strong);margin:0 6px;flex:none}

/* Theme switch */
.mf-switch{position:relative;flex:none;width:60px;height:32px;padding:0;border-radius:999px;cursor:pointer;
  border:1px solid var(--mf-border-strong);background:var(--mf-surface-2);
  transition:background-color .2s,border-color .2s}
.mf-switch:hover{border-color:var(--mf-accent)}
.mf-switch-thumb{position:absolute;top:2px;left:2px;width:26px;height:26px;border-radius:50%;background:var(--mf-grad);
  box-shadow:0 2px 8px rgba(240,58,95,.4);transition:transform .28s cubic-bezier(.4,0,.2,1)}
.mf-switch[aria-checked="true"] .mf-switch-thumb{transform:translateX(28px)}
.mf-switch-track{position:absolute;inset:0;display:flex;align-items:center;justify-content:space-between;padding:0 3px;pointer-events:none}
.mf-switch-track span{width:26px;height:26px;display:grid;place-items:center;color:var(--mf-faint);transition:color .2s}
.mf-switch-track svg{font-size:16px}
.mf-switch[aria-checked="true"] .mf-sw-moon,
.mf-switch[aria-checked="false"] .mf-sw-sun{color:#fff}

/* Match */
.mf-match{flex:none;height:36px;padding:0 16px;border-radius:999px;cursor:pointer;font-size:13px;font-weight:800;letter-spacing:.3px;
  color:var(--mf-text);border:1.5px solid transparent;
  background:linear-gradient(var(--mf-bg-top),var(--mf-bg-top)) padding-box,var(--mf-grad) border-box;
  transition:color .15s,transform .15s,box-shadow .15s}
.mf-match:hover{background:var(--mf-grad) border-box;color:#fff;box-shadow:0 6px 16px rgba(240,58,95,.3);transform:translateY(-1px)}

/* Icon buttons */
.mf-iconbtn{flex:none;width:40px;height:40px;border-radius:12px;display:grid;place-items:center;border:0;cursor:pointer;
  background:transparent;color:var(--mf-muted);transition:background-color .15s,color .15s}
.mf-iconbtn:hover{background:var(--mf-surface-2);color:var(--mf-text)}
.mf-iconbtn.is-active{background:var(--mf-accent-soft);color:var(--mf-accent)}
.mf-iconbtn svg{font-size:22px}

/* Own profile (right end of the bar) */
.mf-me{flex:none;width:40px;height:40px;padding:0;border:0;border-radius:50%;background:transparent;cursor:pointer;display:grid;place-items:center;
  margin-left:2px;transition:transform .15s,box-shadow .15s}
.mf-me:hover{transform:translateY(-1px)}
.mf-me:focus-visible{outline:2px solid var(--mf-accent);outline-offset:2px}
.mf-me .ft-ava{box-shadow:0 0 0 2px var(--mf-bg-top),0 0 0 3.5px var(--mf-accent)}
.mf-login{flex:none;height:34px;padding:0 14px;border-radius:999px;border:1px solid var(--mf-border-strong);background:var(--mf-surface-2);color:var(--mf-text);
  font:inherit;font-size:13px;font-weight:700;cursor:pointer}

/* Overlays */
.mf-overlay{position:fixed;inset:0;z-index:9999;background:rgba(5,7,12,.78)}
.mf-overlay--top{display:flex;align-items:flex-start;justify-content:center;padding:24px;overflow:auto;background:var(--mf-overlay)}
.mf-overlay-inner{max-width:100%}

@media (max-width:719px){
  .mf-nav{flex:1 0 100%;order:3;overflow-x:auto;scrollbar-width:none;padding:2px 0 4px;justify-content:space-between}
  .mf-nav::-webkit-scrollbar{display:none}
  .mf-overlay--top{padding:12px}
}
`;

function injectStyles(id, css) {
  if (typeof document === 'undefined') return;
  let el = document.getElementById(id);
  if (!el) {
    el = document.createElement('style');
    el.id = id;
    document.head.appendChild(el);
  }
  if (el.textContent !== css) el.textContent = css;
}
injectStyles('mf-navbar-css', NAVBAR_CSS);

// Audio call, Video call and Send files now live inside each chat (ChatConversation.js)
const ACTIONS = [
  { key: 'chat', label: 'Chat',    Icon: ChatBubbleOutlineIcon },
  { key: 'live', label: 'Go live', Icon: LiveTvIcon },
];

/**
 * Props:
 *  - theme:         'dark' | 'light'   (owned by Feed)
 *  - onToggleTheme: () => void         (the switch is hidden if not provided)
 */
export default function Navbar({ theme = 'dark', onToggleTheme }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [activeModal, setActiveModal] = useState(null);
  const [friendsFullOpen, setFriendsFullOpen] = useState(false);
  const [matchOpen, setMatchOpen] = useState(false);

  const isDark = theme === 'dark';
  const closeModal = useCallback(() => setActiveModal(null), []);

  useEffect(() => {
    const handler = () => setFriendsFullOpen(true);
    window.addEventListener('openFriendsFull', handler);
    return () => window.removeEventListener('openFriendsFull', handler);
  }, []);

  // Escape closes the top-most overlay (not live, to avoid accidental stops)
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      if (friendsFullOpen) setFriendsFullOpen(false);
      else if (matchOpen) setMatchOpen(false);
      else if (activeModal === 'chat') setActiveModal(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [friendsFullOpen, matchOpen, activeModal]);

  return (
    <>
      <div className="mf-nav navbar-actions" role="toolbar" aria-label="Quick actions">
        {/* DARK / LIGHT SWITCH — sits to the left of Match */}
        {onToggleTheme && (
          <button
            type="button"
            role="switch"
            aria-checked={isDark}
            aria-label="Dark mode"
            title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
            className="mf-switch"
            onClick={onToggleTheme}
          >
            <span className="mf-switch-thumb" aria-hidden="true" />
            <span className="mf-switch-track" aria-hidden="true">
              <span className="mf-sw-sun"><LightModeRoundedIcon /></span>
              <span className="mf-sw-moon"><DarkModeRoundedIcon /></span>
            </span>
          </button>
        )}

        {/* MATCH BUTTON */}
        <button type="button" className="mf-match match-btn" onClick={() => setMatchOpen(true)}>
          Match
        </button>

        <span className="mf-nav-sep" aria-hidden="true" />

        {ACTIONS.map(({ key, label, Icon }) => (
          <button
            key={key}
            type="button"
            className={`mf-iconbtn${activeModal === key ? ' is-active' : ''}`}
            aria-label={label}
            title={label}
            onClick={() => setActiveModal(key)}
          >
            <Icon />
          </button>
        ))}

        <button
          type="button"
          className={`mf-iconbtn${friendsFullOpen ? ' is-active' : ''}`}
          aria-label="Friends"
          title="Friends"
          onClick={() => setFriendsFullOpen(true)}
        >
          <PeopleIcon />
        </button>

        {/* OWN PROFILE — right end of the bar */}
        {user ? (
          <button
            type="button"
            className="mf-me"
            aria-label="My profile"
            title="My profile"
            onClick={() => navigate(`/profile/${user.id}`)}
          >
            <FriendAvatar user={user} size={34} />
          </button>
        ) : (
          <button type="button" className="mf-login" onClick={() => navigate('/login')}>Log in</button>
        )}
      </div>

      {/* Always mounted: they answer incoming calls and file offers on any page */}
      <CallUI />
      <FileTransfer />

      {/* Modals are siblings of the bar, so the bar's horizontal scrolling never affects them */}
      {activeModal && (
        <div className="mf-overlay">
          {activeModal === 'chat' && <ChatModal onClose={closeModal} />}
          {activeModal === 'live' && <LiveStream onClose={closeModal} />}
        </div>
      )}

      {friendsFullOpen && (
        <div
          className="mf-overlay mf-overlay--top"
          role="presentation"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) setFriendsFullOpen(false);
          }}
        >
          <div className="mf-overlay-inner">
            <FriendsPanel mode="full" onClose={() => setFriendsFullOpen(false)} />
          </div>
        </div>
      )}

      {matchOpen && <Match onClose={() => setMatchOpen(false)} />}
    </>
  );
}